import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, filter, map, Observable, of, Subject, takeUntil } from 'rxjs';
import { Customers } from '../../../account-info-header/models/AccountInfoApiResponse';
import { selectEAuthCustomers, selectSubAccountId } from '../../../account-info-header/reducers';
import { selectHasAccessInitiateMoneyMovement, selectHasAccessReleaseMoneyMovement } from '../../../authorize/reducers';
import { ConfirmationPageActions } from '../../../create-request/actions';
import { DraftListTableColumn } from '../../../initiate-and-release/Models/Drafts/DraftResponse';
import { selectSendEmailErrorState } from '../../../setup-eAuthorization/reducers';
import { SharedActions } from '../../../shared/actions';
import { AdobeConstants } from '../../../shared/adobe-tagging/AdobeConstants';
import { AdobeHelpers } from '../../../shared/adobe-tagging/adobeHelper';
import { EAuthInfo } from '../../../shared/models/EAuthInfo';
import { selectEAuthInfo, selectPageScrollTo, selectPdf, selectPreviousContextDifferentToCurrent, selectPreviousCreateTypeInContext } from '../../../shared/reducers';
import { confirmationPageButtons, Constants, eAuthNotifications, navRouteConstants, RegTypeCode } from '../../../shared/utilities/Constants';
import { getArrayBufferFromBase64, openPdfInNewWindow } from '../../../shared/utilities/pdf';
import { positionPage } from '../../../shared/utilities/PrintPageUtils';
import { StandingAuthConfirmationPageActions } from '../../actions';
import { selectHasEauthAccess, selectIsSaPending, selectSAMappedPendReason, selectStandingAuthCanRelease, selectStandingAuthPendForReleaseCustomRule, selectToAccountRegistrationType, selectTransactionType } from '../../reducers';
import { selectInitReleaseTransactionInContext } from '../../../initiate-and-release/reducers';
import { mapInitiatedDraftsToTableColumn } from '../../../create-request/components/transactions-table/table-helpers';

@Component({
  standalone: false,
  selector: 'mm-standing-authorization-confirmation-page',
  templateUrl: './sa-confirmation-page.component.html',
  styleUrls: ['./sa-confirmation-page.component.scss']
})
export class SaConfirmationPageComponent implements OnInit, OnDestroy {
  subAccountId: string;
  standingAuthFormPdf: string;
  eAuthInfo: EAuthInfo;
  isPending$: Observable<boolean>;
  pendReasonDescription$: Observable<string>;
  pendReasonIcon$: Observable<'success' | 'warning'>;
  eAuthInfo$: Observable<EAuthInfo>;
  destroy$ = new Subject<void>();
  transactionType: string | undefined;
  hasEauthAccess: boolean;
  isEAuthRestricted: boolean;
  eAuthCustomers$: Observable<Customers[]>;
  displayAllianceWarning$: Observable<boolean>;
  sendAllianceInstructionsWarningText: string = eAuthNotifications.sendAllianceInstructionsFailedShort;
  i401kConfirmationMessage: string = eAuthNotifications.submitPrefilledFormMessage;
  saLoaNeededText: string = Constants.saLoaNeeded;
  viewButtonText = confirmationPageButtons.viewInR;
  hasInitiatorAccess: boolean;
  hasReleaseAccess: boolean;
  isInitiatorFlow: boolean;
  canReleaseInitiatedTransaction: boolean;
  currentContextDiffersToPrevious: boolean;
  previousContext: string;
  toAccountRegType: string;
  is401kToAccount: boolean = false;
  isNotInitReleaseFlowCustomRules: boolean = false;
  currentDraftInContext$ = new BehaviorSubject<DraftListTableColumn | null>(null);

  private store = inject(Store);
  private router = inject(Router);

  ngOnInit(): void {
    this.store
      .select(selectTransactionType)
      .pipe(takeUntil(this.destroy$))
      .subscribe((val) => this.transactionType = val);

    this.store.pipe(
      takeUntil(this.destroy$),
      select(selectSubAccountId),
    ).subscribe((subAccountId) => {
      this.subAccountId = subAccountId;
    });

    this.eAuthInfo$ = this.store.select(selectEAuthInfo);

    this.store
      .select(selectPageScrollTo)
      .pipe(
        takeUntil(this.destroy$),
        filter((scrollTo) => scrollTo === 'sa-confirmation-page-header')
      )
      .subscribe((_) => {
        this.scrollToTop();
      });

    combineLatest([
      this.store.select(selectHasAccessInitiateMoneyMovement),
      this.store.select(selectStandingAuthCanRelease),
      this.store.select(selectHasAccessReleaseMoneyMovement)
    ]).pipe(takeUntil(this.destroy$), filter(([hasInitiate, _]) => hasInitiate))
      .subscribe(([hasInitiate, canRelease, hasRelease]) => {
        this.hasInitiatorAccess = true;
        this.hasReleaseAccess = hasRelease;
        this.isInitiatorFlow = hasInitiate && !canRelease;
        this.canReleaseInitiatedTransaction = canRelease;
      });


    this.store.select(selectStandingAuthPendForReleaseCustomRule).pipe(takeUntil(this.destroy$))
      .subscribe((noRuleReturned) => {
        this.isNotInitReleaseFlowCustomRules = noRuleReturned;
      });

    this.store.select(selectInitReleaseTransactionInContext).pipe(takeUntil(this.destroy$))
      .subscribe((draft) => {
        this.currentDraftInContext$.next(mapInitiatedDraftsToTableColumn(draft));
      });

    this.store.select(selectEAuthInfo).pipe(takeUntil(this.destroy$))
      .subscribe((info) => this.eAuthInfo = info);
    this.isPending$ = this.store.pipe(select(selectIsSaPending));
    this.pendReasonDescription$ = this.isInitiatorFlow ? of(Constants.initiateInRSLOATransaction) : this.store.pipe(select(selectSAMappedPendReason));
    this.pendReasonIcon$ = this.pendReasonDescription$.pipe(
      map((description) => {
        if (description === Constants.saEauthNeeded || description === Constants.defaultSaPendDescription) {
          return 'success';
        }
        return 'warning';
      })
    );

    this.store
      .select(selectPreviousContextDifferentToCurrent)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (currentContextDiffersToPrevious) =>
          this.currentContextDiffersToPrevious
            = currentContextDiffersToPrevious
      );

    this.store
      .select(selectPreviousCreateTypeInContext)
      .pipe(takeUntil(this.destroy$))
      .subscribe((previousContext) => this.previousContext = previousContext);


    this.store.select(selectHasEauthAccess).pipe(takeUntil(this.destroy$))
      .subscribe((hasEauthAccess) => this.hasEauthAccess = hasEauthAccess);

    this.eAuthCustomers$ = this.store.select(selectEAuthCustomers);

    this.store.pipe(
      takeUntil(this.destroy$),
      select(selectPdf)
    ).subscribe((data) => {
      if (data) {
        this.standingAuthFormPdf = data;
        const buffer = getArrayBufferFromBase64(data);
        const standingAuthFormPdf = new Blob([buffer], { type: 'application/pdf' });
        openPdfInNewWindow(standingAuthFormPdf, false);
      }
    });

    this.displayAllianceWarning$ = this.store.select(selectSendEmailErrorState);

    this.store.select(selectToAccountRegistrationType).pipe(
      takeUntil(this.destroy$)
    )
      .subscribe((toAccountRegType) => {
        this.toAccountRegType = toAccountRegType;
        this.is401kToAccount = this.toAccountRegType === RegTypeCode.INDIVIDUAL_401K || this.toAccountRegType === RegTypeCode.ROTH_401K;
      });
  }

  downloadPdf(): void {
    setTimeout(() => {
      const temp = document.title;
      document.title = 'Move Money - Schwab Advisor Center';
      try {
        positionPage('print-confirmation-page');
        if (!document.execCommand('print', false, '')) {
          window.print();
        }
      } catch {
        positionPage('print-confirmation-page');
        window.print();
      }
      document.title = temp;
    }, 10);
  }

  get showLoaButton(): boolean {
    const isI401KToAccount: boolean = this.toAccountRegType === RegTypeCode.INDIVIDUAL_401K || this.toAccountRegType === RegTypeCode.ROTH_401K;
    //Always return true if to account is I401K and everytime Authorization form should be visible
    if (isI401KToAccount) {
      return true;
    }
    if (this.hasInitiatorAccess) {
      return this.eAuthInfo?.generateLoaPdf && this.eAuthInfo?.isOptedOut && this.canReleaseInitiatedTransaction;
    }
    return this.eAuthInfo?.generateLoaPdf && this.eAuthInfo?.isOptedOut;
  }

  get showClientAuthorizationDetails(): boolean {
    if (this.hasInitiatorAccess && !this.canReleaseInitiatedTransaction && this.eAuthInfo.isOptedOut) {
      return false;
    }
    return true;
  }

  get isInrReleasorFlow(): boolean {
    return this.canReleaseInitiatedTransaction;
  }

  get isInRFirm(): boolean {
    return !!this.hasInitiatorAccess;
  }

  get customRuleInRFlowOff(): boolean {
    return this.isNotInitReleaseFlowCustomRules && !this.canReleaseInitiatedTransaction && this.isInRFirm;
  }

  onCreateNewStandingAuthClicked(): void {
    this.store.dispatch(StandingAuthConfirmationPageActions.createNewStandingAuthClicked({ accountId: this.subAccountId }));
    AdobeHelpers.fireClick(AdobeConstants.LINKID + AdobeConstants._CREATE_NEW_STANDING_AUTH);
    if (this.router?.url?.includes('initiate-and-release')) {
      this.router.navigate([navRouteConstants.standingAuth]);
    }
  }

  onReturnStandingAuthClicked(): void {
    this.store.dispatch(StandingAuthConfirmationPageActions.returnStandingAuthClicked({ accountId: this.subAccountId }));
    if (this.shouldReturnToPreviousContext) {
      this.router.navigate([this.navRouteString]);
      this.store.dispatch(SharedActions.previousContextNavigation());
    }
  }
  scrollToTop(): void {
    const elem = document.getElementById('sa-confirmation-page-header');
    setTimeout(function() {
      elem?.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
        inline: 'start'
      });
    });
  }

  onViewAuthFormClicked(): void {
    if (this.standingAuthFormPdf) {
      const buffer = getArrayBufferFromBase64(this.standingAuthFormPdf);
      const pdf = new Blob([buffer], { type: 'application/pdf' });
      openPdfInNewWindow(pdf, false);
    }
  }

  get shouldReturnToPreviousContext(): boolean {
    return this.currentContextDiffersToPrevious;
  }

  get navRouteString(): string {
    switch (this.previousContext) {
      case 'standing-authorization': return navRouteConstants.standingAuth;
      case 'transaction': return navRouteConstants.createTransaction;
      default: return navRouteConstants.createTransaction;
    }
  }

  // Left of the OR stametent makes sure button shows up for releaser flow & right of the OR makes sure we show it in initiator flow when a rule is met
  get shouldDisplayViewInitiateButton(): boolean {
    return this.hasInitiatorAccess && this.isInitiateAndReleaseFlowFromTable || this.hasInitiatorAccess && !this.isInitiateAndReleaseFlowFromTable && !this.canReleaseInitiatedTransaction;
  }

  get isInitiateAndReleaseFlowFromTable(): boolean {
    return !!this.isInRFirm && !!this.currentDraftInContext$.value?.DraftId;
  }

  onViewTransactionStatusClicked(): void {
    this.store.dispatch(ConfirmationPageActions.viewTransactionStatusClicked({ accountId: this.subAccountId }));
    this.router.navigate([navRouteConstants.initiateAndRelease]);
  }

  ngOnDestroy(): void {
    this.destroy$?.next();
    this.destroy$?.unsubscribe();
  }
}
